import React, { useState } from 'react';
import { BarChart3Icon, TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
type Notification = {
  id: string;
  event: string;
  date: string;
  impact: 'high_negative' | 'medium_negative' | 'low_positive' | 'medium_positive' | 'high_positive';
  acknowledgedBy: string | null;
};
const notifications: Notification[] = [{
  id: '1',
  event: 'DBT increased from 6 days to 10 days',
  date: '3/16/2026',
  impact: 'medium_negative',
  acknowledgedBy: null
}, {
  id: '2',
  event: 'Pulse Score increased from 74 to 82 (+8 points)',
  date: '2/20/2026',
  impact: 'high_positive',
  acknowledgedBy: 'Dave Davidson'
}, {
  id: '3',
  event: 'Strong revenue growth reported in Q1 financials',
  date: '1/15/2026',
  impact: 'high_positive',
  acknowledgedBy: 'Dave Davidson'
}];
function ImpactIndicator({
  impact
}: {
  impact: Notification['impact'];
}) {
  if (impact === 'high_negative') {
    return <div className="flex items-end gap-0.5 justify-center" data-id="element-2767">
        <div className="w-1.5 h-2 bg-red-500 rounded-sm" data-id="element-2768" />
        <div className="w-1.5 h-3.5 bg-red-500 rounded-sm" data-id="element-2769" />
        <div className="w-1.5 h-5 bg-red-500 rounded-sm" data-id="element-2770" />
      </div>;
  }
  if (impact === 'medium_negative') {
    return <div className="flex items-end gap-0.5 justify-center" data-id="element-2771">
        <div className="w-1.5 h-2 bg-red-500 rounded-sm" data-id="element-2772" />
        <div className="w-1.5 h-3.5 bg-red-500 rounded-sm" data-id="element-2773" />
        <div className="w-1.5 h-5 bg-gray-200 rounded-sm" data-id="element-2774" />
      </div>;
  }
  if (impact === 'low_positive') {
    return <div className="flex items-end gap-0.5 justify-center" data-id="element-2775">
        <div className="w-1.5 h-2 bg-green-500 rounded-sm" data-id="element-2776" />
        <div className="w-1.5 h-3.5 bg-gray-200 rounded-sm" data-id="element-2777" />
        <div className="w-1.5 h-5 bg-gray-200 rounded-sm" data-id="element-2778" />
      </div>;
  }
  if (impact === 'medium_positive') {
    return <div className="flex items-end gap-0.5 justify-center" data-id="element-2779">
        <div className="w-1.5 h-2 bg-green-500 rounded-sm" data-id="element-2780" />
        <div className="w-1.5 h-3.5 bg-green-500 rounded-sm" data-id="element-2781" />
        <div className="w-1.5 h-5 bg-gray-200 rounded-sm" data-id="element-2782" />
      </div>;
  }
  // high_positive
  return <div className="flex items-end gap-0.5 justify-center" data-id="element-2783">
      <div className="w-1.5 h-2 bg-green-500 rounded-sm" data-id="element-2784" />
      <div className="w-1.5 h-3.5 bg-green-500 rounded-sm" data-id="element-2785" />
      <div className="w-1.5 h-5 bg-green-500 rounded-sm" data-id="element-2786" />
    </div>;
}
export function NotificationsTab() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleAll = () => {
    if (selected.size === notifications.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(notifications.map(n => n.id)));
    }
  };
  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelected(next);
  };
  return <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-2787">
      <div className="p-5 border-b border-gray-200" data-id="element-2788">
        <h3 className="text-lg font-bold text-gray-900" data-id="element-2789">Notifications</h3>
      </div>
      <div className="overflow-x-auto" data-id="element-2790">
        <table className="w-full text-sm text-left" data-id="element-2791">
          <thead className="bg-gray-50 text-gray-500 border-b border-gray-200" data-id="element-2792">
            <tr data-id="element-2793">
              <th className="px-5 py-3 w-10" data-id="element-2794">
                <input type="checkbox" checked={selected.size === notifications.length} onChange={toggleAll} className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" data-id="element-2795" />
              </th>
              <th className="px-5 py-3 font-medium" data-id="element-2796">Event</th>
              <th className="px-5 py-3 font-medium text-center w-32" data-id="element-2797">Date</th>
              <th className="px-5 py-3 font-medium text-center w-24" data-id="element-2798">Impact</th>
              <th className="px-5 py-3 font-medium text-center w-40" data-id="element-2799">
                Acknowledged By
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-2800">
            {notifications.map(n => <tr key={n.id} className="hover:bg-gray-50 transition-colors" data-id="element-2801">
                <td className="px-5 py-4" data-id="element-2802">
                  <input type="checkbox" checked={selected.has(n.id)} onChange={() => toggleOne(n.id)} className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer" data-id="element-2803" />
                </td>
                <td className="px-5 py-4 text-gray-900" data-id="element-2804">{n.event}</td>
                <td className="px-5 py-4 text-gray-500 text-center whitespace-nowrap" data-id="element-2805">
                  {n.date}
                </td>
                <td className="px-5 py-4 text-center" data-id="element-2806">
                  <ImpactIndicator impact={n.impact} data-id="element-2807" />
                </td>
                <td className="px-5 py-4 text-gray-500 text-center" data-id="element-2808">
                  {n.acknowledgedBy ?? '--'}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-end gap-4 px-5 py-3 border-t border-gray-200 text-sm text-gray-500" data-id="element-2809">
        <span data-id="element-2810">Rows per page: 5</span>
        <span data-id="element-2811">1–5 of 5</span>
        <div className="flex gap-1" data-id="element-2812">
          <button className="p-1 rounded hover:bg-gray-100 text-gray-400 cursor-not-allowed" data-id="element-2813">
            ‹
          </button>
          <button className="p-1 rounded hover:bg-gray-100 text-gray-400 cursor-not-allowed" data-id="element-2814">
            ›
          </button>
        </div>
      </div>
    </div>;
}